import type { ReactNode } from 'react';
import type { NavItem } from '../api/alpha';
import { Breadcrumbs } from '../components/Breadcrumbs';
import {
  GlobalFilterBar,
  GlobalFilterProvider,
  GlobalFilterShellBridge,
} from '../global-filter';
import { ShellProvider, useShell } from './ShellProvider';
import { ShellSidebar } from './ShellSidebar';
import { ShellTopBar } from './ShellTopBar';
import { StatusFooter } from './StatusFooter';
import { shell } from './shellClasses';

function AppShellFrame({ children }: { children: ReactNode }) {
  const { collapsed, mobileOpen, setMobileOpen } = useShell();
  const width = collapsed ? 'w-16 md:w-16' : 'w-64';

  return (
    <div className={`flex min-h-dvh ${shell.canvas}`}>
      <aside
        className={`fixed inset-y-0 left-0 z-40 transform transition-transform duration-[var(--shell-motion-normal)] motion-reduce:transition-none md:static md:translate-x-0 ${width} ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <ShellSidebar onNavigate={() => setMobileOpen(false)} />
      </aside>

      {mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* One sticky chrome block: identity + trust + context. No other toolbars. */}
        <div className="sticky top-0 z-20">
          <ShellTopBar />
          <GlobalFilterBar />
        </div>
        <main id="main-content" className="flex-1 px-4 py-6 lg:px-8 lg:py-8" tabIndex={-1}>
          <Breadcrumbs />
          <div className="shell-content min-w-0">{children}</div>
        </main>
        <StatusFooter />
      </div>
    </div>
  );
}

export function AppShell({ nav, children }: { nav: NavItem[]; children: ReactNode }) {
  return (
    <ShellProvider allNav={nav}>
      {/* GFE must nest inside Router + ShellProvider (URL + workspace labels). */}
      <GlobalFilterProvider>
        <GlobalFilterShellBridge />
        <AppShellFrame>{children}</AppShellFrame>
      </GlobalFilterProvider>
    </ShellProvider>
  );
}

export { WorkspaceHeader, ActionBar } from './WorkspaceHeader';
export { ShellSidebar } from './ShellSidebar';
export { useShell } from './ShellProvider';
export {
  FOUNDER_NAV_ORDER,
  buildFounderNav,
  toFounderNav,
  resolveShellNav,
  rankNavForCommand,
} from './navConfig';
